import {CSSProperties, ReactNode, useEffect} from 'react';
import ButtonSolid from 'components/Buttons/ButtonSolid';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import Analytics from 'utils/Analytics';
import {readSeverityFromUrl} from 'surveys/osa_funnel/survey/severity';
import {useRevealOnView} from './useRevealOnView';
import styles from './EducationShell.module.css';

/** Wrap content inline; fades + slides up the first time it enters the viewport. */
export const RevealBlock = ({
  children,
  delayMs = 0,
  className,
  style,
}: {
  children: ReactNode;
  delayMs?: number;
  className?: string;
  style?: CSSProperties;
}) => {
  const {ref, triggered} = useRevealOnView();
  return (
    <div
      ref={ref}
      className={`${styles.reveal} ${triggered ? styles.revealOn : ''} ${className ?? ''}`}
      style={{...style, transitionDelay: triggered ? `${delayMs}ms` : '0ms'}}
    >
      {children}
    </div>
  );
};

/** Smaller fade-up reveal — slightly shorter travel + duration. */
export const FadeUp = ({
  children,
  delayMs = 0,
  className,
  style,
}: {
  children: ReactNode;
  delayMs?: number;
  className?: string;
  style?: CSSProperties;
}) => {
  const {ref, triggered} = useRevealOnView();
  return (
    <div
      ref={ref}
      className={`${styles.fadeUp} ${triggered ? styles.fadeUpOn : ''} ${className ?? ''}`}
      style={{...style, transitionDelay: triggered ? `${delayMs}ms` : '0ms'}}
    >
      {children}
    </div>
  );
};

/** Optional intro at the top of an education screen: headline + subhead.
 *  No splash / no logo on web — the survey shell already renders the RISE
 *  + SLEEP APNEA RISK pill above. */
export const ScreenIntro = ({
  headline,
  subhead,
  eyebrow,
}: {
  headline: ReactNode;
  subhead?: ReactNode;
  eyebrow?: ReactNode;
}) => (
  <div className={styles.intro}>
    {eyebrow ? <FadeUp className={styles.eyebrowBlock}>{eyebrow}</FadeUp> : null}
    <FadeUp delayMs={eyebrow ? 150 : 0}>
      <h2 className={styles.headline}>{headline}</h2>
    </FadeUp>
    {subhead ? (
      <FadeUp delayMs={eyebrow ? 300 : 150}>
        <p className={styles.subhead}>{subhead}</p>
      </FadeUp>
    ) : null}
  </div>
);

/** Cards: panel surface, rounded, border. */
export const Card = ({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) => (
  <div className={`${styles.card} ${className ?? ''}`} style={style}>
    {children}
  </div>
);

export const CardTitle = ({children, className}: {children: ReactNode; className?: string}) => (
  <h3 className={`${styles.cardTitle} ${className ?? ''}`}>{children}</h3>
);

export const CardCaption = ({children, className}: {children: ReactNode; className?: string}) => (
  <div className={`${styles.cardCaption} ${className ?? ''}`}>{children}</div>
);

/** Small uppercase eyebrow above a section heading. */
export const Eyebrow = ({children, color, className}: {children: ReactNode; color?: string; className?: string}) => (
  <span className={`${styles.eyebrow} ${className ?? ''}`} style={color ? {color} : undefined}>
    {children}
  </span>
);

/** Inline brand-gold accent (Zepbound brand). Uses deep amber on light bg. */
export const Gold = ({children}: {children: ReactNode}) => <span className={styles.gold}>{children}</span>;

/** Inline ® mark, gold by default, tuned to sit slightly above baseline. */
export const Registered = ({size = 11}: {size?: number} = {}) => (
  <sup className={styles.registered} style={{fontSize: size}}>
    ®
  </sup>
);

export const ScreenSubhead = ({children, className}: {children: ReactNode; className?: string}) => (
  <h4 className={`${styles.screenSubhead} ${className ?? ''}`}>{children}</h4>
);

export const ScreenBody = ({children, className}: {children: ReactNode; className?: string}) => (
  <p className={`${styles.screenBody} ${className ?? ''}`}>{children}</p>
);

/** FDA badge + body text row. Used for regulatory credibility. */
export const FDARow = ({text, className}: {text: ReactNode; className?: string}) => (
  <div className={`${styles.fdaRow} ${className ?? ''}`}>
    <FDABadge />
    <span className={styles.fdaRowText}>{text}</span>
  </div>
);

const FDABadge = () => (
  <svg viewBox="0 0 56 24" width="56" height="24" aria-hidden="true" className={styles.fdaBadge}>
    <rect x="0.5" y="0.5" width="55" height="23" rx="3" fill="none" stroke="currentColor" strokeWidth="1" />
    <text x="28" y="17" textAnchor="middle" fontFamily="serif" fontSize="13" fontWeight="700" fill="currentColor">
      FDA
    </text>
  </svg>
);

/** Square Rx pharmacy icon. */
export const PharmacyGlyph = ({size = 42}: {size?: number}) => (
  <svg viewBox="0 0 42 42" width={size} height={size} aria-hidden="true">
    <rect x="3" y="3" width="36" height="36" rx="8" fill="none" stroke="currentColor" strokeOpacity="0.5" />
    <text
      x="21"
      y="27"
      textAnchor="middle"
      fontFamily="Times New Roman, serif"
      fontWeight="700"
      fontStyle="italic"
      fontSize="22"
      fill="currentColor"
    >
      Rx
    </text>
  </svg>
);

export const RxBadgeGlyph = ({size = 28}: {size?: number}) => (
  <svg viewBox="0 0 28 28" width={size} height={size} aria-hidden="true">
    <rect x="2" y="2" width="24" height="24" rx="6" fill="none" stroke="currentColor" strokeOpacity="0.5" />
    <text
      x="14"
      y="19"
      textAnchor="middle"
      fontFamily="Times New Roman, serif"
      fontWeight="700"
      fontStyle="italic"
      fontSize="14"
      fill="currentColor"
    >
      Rx
    </text>
  </svg>
);

export const ShieldGlyph = ({size = 36}: {size?: number}) => (
  <svg viewBox="0 0 36 36" width={size} height={size} aria-hidden="true">
    <path
      d="M18 4 L29 8 v8 c0 8 -5 14 -11 16 -6 -2 -11 -8 -11 -16 v-8 z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M12 18 L16.5 22.5 L24.5 14"
      fill="none"
      stroke="var(--osa-accent-healthy)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Fixed-bottom CTA matching the OSA flow's existing button pattern. */
export const CTABar = ({label, onClick, disabled}: {label: string; onClick: () => void; disabled?: boolean}) => (
  <div className={styles.ctaBar}>
    <ButtonSolid title={label} onClick={onClick} disabled={disabled} />
  </div>
);

/** Root container for an education interstitial. Mirrors ScienceScreen
 *  pattern: top spacing, fixed-bottom CTA, white-space normal, content
 *  padded so the CTA never covers the last block. */
export const EducationScreen = ({
  children,
  ctaLabel = 'Next',
  analyticsShown,
  analyticsSubmitted,
  variant,
}: {
  children: ReactNode;
  ctaLabel?: string;
  analyticsShown?: string;
  analyticsSubmitted?: string;
  variant?: 'branded_v1' | 'compounded_v1';
}) => {
  useEffect(() => {
    if (analyticsShown) {
      Analytics.track(analyticsShown, {severity: readSeverityFromUrl(), variant});
    }
    // SurveyJS swaps page content without resetting document scroll, so a tall
    // previous screen leaves the next one mounted below the fold. Snap to top
    // so each education screen lands with its header in view. Double rAF so we
    // beat SurveyJS's post-render layout side-effects.
    requestAnimationFrame(() => {
      window.scrollTo({top: 0, behavior: 'auto'});
      requestAnimationFrame(() => window.scrollTo({top: 0, behavior: 'auto'}));
    });
  }, [analyticsShown, variant]);

  const handleNext = () => {
    if (analyticsSubmitted) {
      Analytics.track(analyticsSubmitted, {severity: readSeverityFromUrl(), variant});
    }
    if (surveyInstance.isLastPage) {
      surveyInstance.completeLastPage();
    } else {
      surveyInstance.nextPage();
    }
  };

  return (
    <div className={styles.container} data-testid="osa-education">
      <div className={styles.body}>{children}</div>
      <CTABar label={ctaLabel} onClick={handleNext} />
    </div>
  );
};
